import { alpha, createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

const components: ThemeOptions['components'] = {
  MUIDataTableToolbar: {
    styleOverrides: {
      root: {
        display: 'none',
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        outline: 0,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '4px 8px',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      head: {
        height: '56px',
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      option: ({ theme }) => ({
        '&.Mui-focused': {
          backgroundColor: `${alpha(theme.palette.text.primary, 0.09)} !important`,
        },
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
        boxShadow: theme.palette.mode === 'dark' ? '0px 2px 8px rgba(0, 0, 0, 0.6)' : '0px 2px 8px rgba(0, 0, 0, 0.15)',
        fontSize: '0.8125rem',
        maxWidth: 400,
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.background.paper,
        '&::before': {
          border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
        },
      }),
    },
  },
};

// Brand / CI colors — Intended to be swapped
const BRAND = {
  primary: '#D1CDC2',
  secondary: '#64FC40',
  //primary: '#317FFF',
  //secondary: '#40CC2C',
};

// Mode-invariant palette pieces. Anything that should be the same in light
// and dark goes here; the per-mode palettes only declare what differs.
const sharedPalette: ThemeOptions['palette'] = {
  common: { black: '#000000', white: '#FFFFFF' },
  primary: { main: BRAND.primary },
  secondary: { main: BRAND.secondary },
  info: { main: '#999999', contrastText: '#FFFFFF' },
};

const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  background: {
    default: '#EFEEEA',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#000000',
    secondary: '#999999',
  },
};

const darkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  background: {
    default: '#121212',
    paper: '#1E1E1E',
  },
  text: {
    primary: '#F4F4F4',
    secondary: '#B3B3B3',
  },
};

export const createAppTheme = (mode: PaletteMode): Theme => {
  const palette: ThemeOptions['palette'] = {
    ...sharedPalette,
    ...(mode === 'dark' ? darkPalette : lightPalette),
  };

  const theme = createTheme({ components, palette });

  // Custom palette keys don't get light/dark/contrastText auto-derived by
  // createTheme — that only happens for MUI's built-in keys. Augment manually.
  // Header band reads as a dark band against the light page and as a clearly
  // lifted band against the dark page; same value in both modes.
  theme.palette.header = theme.palette.augmentColor({
    color: { main: '#444444' },
    name: 'header',
  });

  return theme;
};
